import React from 'react';
import { makeStyles } from '@material-ui/styles';
import cn from 'clsx';
import ButtonBase from '../BaseButton';

const MainButton: React.FC<ButtonProps> = props => {
    const {
        variant = 'stroke',
        size,
        fullWidth,
        before,
        after,
        className,
        children,
        ...domProps
    } = props;
    const classes = useStyles();
    const classNames = cn(
        classes.root,
        classes[variant],
        size && classes[size],
        fullWidth && classes.fullWidth,
        className
    );

    return (
        <ButtonBase className={classNames} {...domProps}>
            {before && <div className={classes.surroundings}>{before}</div>}
            <div className={classes.text}>{children}</div>
            {after && <div className={classes.surroundings}>{after}</div>}
        </ButtonBase>
    );
};

const useStyles = makeStyles(
    ({ palette, typography, customization, mixBlendMode }) => {
        const disabledStroke = {
            pointerEvents: 'none',
            color: palette.greys[400],
            border: `1px solid ${palette.greys[400]}`,
        };
        const disabledFill = {
            cursor: 'not-allowed',
            pointerEvents: 'none',
            backgroundColor: palette.greys[400],
        };

        return {
            root: {
                ...typography.t70,
                lineHeight: 1.45,
                transition: 'all 300ms ease-in-out',
                display: 'flex',
                alignItems: 'center',
                maxWidth: '100%',
                borderRadius: 30,
                padding: '.5em 1em',
                '& > * + *': {
                    marginLeft: '.5em',
                },
            },
            surroundings: {
                display: 'flex',
                alignItems: 'center',
                ...typography.t50,
                flexGrow: 0,
                flexShrink: 0,
                '&:first-child': { marginLeft: -1 },
                '&:last-child': { marginRight: -1 },
            },
            text: {
                textAlign: 'center',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                flexGrow: 1,
            },
            fill: {
                color: palette.greys[100],
                backgroundColor: palette.greys[700],
                '&:hover': {
                    backgroundColor: palette.greys[600],
                },
                '&:disabled': disabledFill,
            },
            fillPrimary: {
                color: customization.interactive.G10,
                backgroundColor: customization.interactive.C10,
                '&:hover': {
                    color: customization.interactive.G10,
                    backgroundColor: customization.interactive.C20,
                },
                '&:disabled': disabledFill,
            },
            stroke: {
                color: palette.greys[700],
                border: `1px solid ${palette.greys[700]}`,
                '&:hover': {
                    backgroundColor: `${palette.greys[700]}10`,
                    mixBlendMode: mixBlendMode,
                },
                '&:disabled': disabledStroke,
            },
            strokePrimary: {
                color: palette.primary[500],
                border: `1px solid ${palette.primary[500]}`,
                '&:hover': {
                    backgroundColor: `${palette.greys[700]}10`,
                    mixBlendMode: mixBlendMode,
                },
                '&:disabled': disabledStroke,
            },
            large: {
                padding: '.75em 1em',
            },
            small: {
                padding: '0 0.75em',
                ...typography.t80,
            },
            fullWidth: {
                width: '100%',
                justifyContent: 'center',
                '& > *:nth-child(2)': {
                    flexGrow: 0,
                },
            },
        };
    },
    { classNamePrefix: 'MainButton' }
);

type ButtonProps = JSX.IntrinsicElements['button'] & {
    variant?: 'stroke' | 'strokePrimary' | 'fill' | 'fillPrimary' | 'tertiary';
    size?: 'large' | 'small';
    before?: React.ReactNode;
    after?: React.ReactNode;
    fullWidth?: boolean;
};

export default MainButton;
